/* ──────────────────────────────────────────────────────────────────────────
 *  Dashboard 4.0 – modernised layout w/ ZapFeed 4.0
 *  Location:  src/Pages/User/Dashboard/index.js
 *  Features (Jul 2025)
 *    • Swaps legacy UnifiedUserFeed → brand-new ZapFeed (same component used by creators)
 *    • Fully responsive: single-column on mobile, two-column on ≥md
 *    • Floating globe is hidden on <md, parallax-like fade on scroll
 *    • Smooth cards, glass-morphism touches, tighter spacing on small screens
 *    • Right-rail now scroll-locks on desktop for easy feed reading
 * ──────────────────────────────────────────────────────────────────────────*/
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  Button,
  styled,
  alpha,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Globe from "./globe";
import useAuth from "../../../Hooks/use-auth";
import API from "../../../API";
import { useQuery } from "react-query";
import BlitzBoltsShowcase from "./blitzbolts";
import ZapFeed from "../../../CreatorUser/CreatorPages/Dashboard/zapfeed/zapfeed"; // ← new

/* ──────────────────────────────────────────────────────────────────
 *  Styled helpers
 * ─────────────────────────────────────────────────────────────────*/
const FloatingGlobeContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  width: 450,
  height: 450,
  opacity: 0.4,
  pointerEvents: "none",
  zIndex: 0,
  [theme.breakpoints.down("md")]: { display: "none" },
}));

const ReferralCard = styled(Card)(({ theme }) => ({
  position: "relative",
  borderRadius: 24,
  overflow: "hidden",
  backdropFilter: "blur(10px)",
  background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(
    theme.palette.background.default,
    0.3
  )})`,
}));

/* ──────────────────────────────────────────────────────────────────*/
export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down("md"));
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();

  /* server data */
  const { data: dashboardData } = useQuery({
    queryKey: ["user", "dashboard"],
    queryFn: API.users.dashboard,
    refetchOnWindowFocus: false,
  });

  const referralsCount = dashboardData?.referralsCount || 0;
  const referralProgress = (referralsCount / 5) * 100;
  const catalogCampaigns = dashboardData?.catalog_campaigns || [];

  /* ───────────────────────────────────────────────────────────────*/
  return (
    <Box sx={{ position: "relative", minHeight: "100vh", px: { xs: 1, sm: 2, md: 3 } }}>
      {/* decorative globe */}
      <FloatingGlobeContainer>
        <Globe size={450} />
      </FloatingGlobeContainer>

      {/* ─── HEADER / BLITZ BOLTS ───────────────────────────────── */}
      <Box sx={{ pt: { xs: 8, md: 12 }, mb: { xs: 4, md: 6 } }}>
        <BlitzBoltsShowcase userId={userInfo?.id} limit={4} referralsCount={referralsCount} />
      </Box>

      {/* ─── MAIN GRID (stack → columns) ────────────────────────── */}
      <Grid container spacing={downMd ? 2 : 4} alignItems="flex-start">
        {/* FEED left column */}
        <Grid item xs={12} md={8}>
          <ZapFeed />
        </Grid>

        {/* RIGHT rail – sticky on desktop */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: "sticky" }, top: { md: 96 }, display: "flex", flexDirection: "column", gap: 3 }}>            

            {/* Featured Catalogs */}
            {catalogCampaigns.length > 0 && (
              <Card variant="outlined" sx={{ p: 3, borderRadius: 4, backdropFilter: "blur(6px)" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Featured Catalogs
                </Typography>
                {catalogCampaigns.map((c) => (
                  <Card key={c.id} elevation={0} sx={{ display: "flex", mb: 1.5, p: 1.5, borderRadius: 3, cursor: "pointer", "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.3) } }} onClick={() => navigate(`/catalog/${c.id}`)}>
                    <Box component="img" src={c.campaign_photo || "/placeholder.png"} alt={c.name} sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: 2, mr: 2 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        About: {c.brief}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ${parseFloat(c.campaign_sum).toLocaleString()}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Card>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}